import { Helmet } from "react-helmet-async";

import { SEO_ENABLED } from "@/libs/configs";

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: "website" | "article" | "event" | "organization";
  structuredData?: object;
  canonical?: string;
  noindex?: boolean;
}

const BASE_KEYWORDS = new Set<string>([
  "sự kiện",
  "event",
  "cộng đồng",
  "community",
  "kết nối",
  "connect",
  "workshop",
  "networking"
]);

export function SEOHead({
  title = "Garther - Nơi những cuộc gặp gỡ trở nên ý nghĩa",
  description = "Garther giúp bạn tìm kiếm, đặt vé và tham gia các sự kiện thú vị – nơi con người kết nối, học hỏi và sẻ chia trong không gian gần gũi.",
  keywords = [],
  image = "https://garther.app/logo.png",
  url,
  type = "website",
  structuredData,
  canonical,
  noindex = false
}: SEOHeadProps) {
  // If SEO is disabled, return null
  if (!SEO_ENABLED) {
    return null;
  }

  const fullUrl = url ? `https://garther.app${url}` : "https://garther.app";
  const fullImage = image.startsWith("http") ? image : `https://garther.app${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={[...new Set<string>([...keywords, ...BASE_KEYWORDS])].join(", ")} />
      <meta name="author" content="Garther" />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow"} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Garther" />
      <meta property="og:locale" content="vi_VN" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:site" content="@garther" />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#F6CBCB" />
      <meta name="msapplication-TileColor" content="#F6CBCB" />

      {/* Structured Data */}
      {structuredData && <script type="application/ld+json">{JSON.stringify(structuredData)}</script>}
    </Helmet>
  );
}
